/* eslint-disable react/no-unknown-property */
import React, { useCallback, useMemo } from 'react';
import Image from 'next/image';
import useTypedTranslation from '@/hooks/useTypedTranslation';
import { CommonEnum } from '@/types/translations/CommonEnum';
import { Accordion, Col, Container } from 'react-bootstrap';
import { PrimaryButtonLink } from '../common/PrimaryButton';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Autoplay } from 'swiper/modules';
import { LandingPageProps } from '@/types/props/components/skus';
import SkusEntirePage from '../skus/SkusEntirePage';
import envPublic from '@/config/env.public';
import CategoryNav from '@/components/home/categoryNav';
import { BlogEnum } from '@/types/translations/BlogEnum';
import RelatedBlogs from '@/components/blog/related_blogs';
import IndexNav from '@/components/home/Index/IndexNav/IndexNav';
import strapiConstants from '@/constants/strapi.constants';
import checkArray from '@/utilites/check-array';
import { useQueries } from '@tanstack/react-query';
import getNewSkus from '@/api/getNewSkus';
import getCollectionsUseQuery from '@/api/getCollectionsUseQuery';
import { useRouter } from 'next/router';
import { SkuDTO } from '@/types/api/sku';
// FAQ Section with enhanced interactivity
const FAQSection: React.FC<{
  faqs?: Record<string, { question: string; answer: string }> | null;
}> = ({ faqs }) => {
  if (!faqs || Object.keys(faqs).length === 0) return null;

  return (
    <div className={`faqsection tw-mt-[60px] tw-bg-customGray tw-py-[60px]`}>
      <Container>
        <div className="tw-align-items-center tw-mb-[40px] tw-flex tw-justify-between max-sm:tw-flex-col max-sm:tw-text-center">
          <h2>
            Do you have any questions? <br />
          </h2>
          <PrimaryButtonLink
            href={'mailto:info@spiritory.com'}
            outlined={true}
            className={
              'd-flex font-weight-bold justify-content-center tw-px-[60px] max-sm:tw-m-auto max-sm:tw-mt-[15px]'
            }
          >
            Contact Us
          </PrimaryButtonLink>
        </div>

        <Accordion>
          {Object.entries(faqs).map(([key, item], index) => (
            <Accordion.Item eventKey={`${index}`} key={key}>
              <Accordion.Header>
                {item.question} {/* Zugriff auf die `question`-Eigenschaft */}
              </Accordion.Header>

              <Accordion.Body>
                {item.answer} {/* Zugriff auf die `answer`-Eigenschaft */}
              </Accordion.Body>
            </Accordion.Item>
          ))}
        </Accordion>
      </Container>
    </div>
  );
};

// Helper function for preparing query data
const prepareQueryData = <T,>(data: any): T[] => {
  return (checkArray(data) ? data : []) as T[];
};

// Shared React Query config to reduce repetition
const QUERY_CONFIG = {
  staleTime: 5 * 60 * 1000,
  refetchOnWindowFocus: false,
  retry: 1,
};

export default function LandingPage({
  landingPageProps,
  skusServerSideProps,
  slug,
}: LandingPageProps) {
  const { locale } = useRouter();

  const { translate: translateBlog } = useTypedTranslation<BlogEnum>('blog');

  const { landingPage: page } = landingPageProps || {};

  const selectSkus = useCallback((data: any) => prepareQueryData<SkuDTO>(data), []);

  const [collectionsQuery, newSkusQuery] = useQueries({
    queries: [
      {
        ...QUERY_CONFIG,
        queryKey: ['collections', strapiConstants.queries.collection, locale],
        queryFn: getCollectionsUseQuery,
      },
      {
        ...QUERY_CONFIG,
        queryKey: ['newSkus'],
        queryFn: getNewSkus,
        select: selectSkus,
      },
    ],
  });

  const newCollections = useMemo(() => {
    const collections = collectionsQuery.data || [];
    const _newCollections = collections.sort((a, b) => {
      const dateA = new Date(a.attributes?.createdAt || '');
      const dateB = new Date(b.attributes?.createdAt || '');
      return dateB.getTime() - dateA.getTime();
    });
    const newCollections = _newCollections.slice(0, 5);
    return newCollections.map(collection => ({
      name: collection?.attributes?.name || '',
      href: `/collections/${collection.attributes?.slug}`,
    }));
  }, [collectionsQuery.data]);

  const newSkus = useMemo(() => {
    const skus = newSkusQuery.data || [];
    const _newSkus = skus.slice(0, 5).map(sku => ({
      name: sku?.name || '',
      href: `/${sku.slug}`,
    }));
    return _newSkus;
  }, [newSkusQuery.data]);

  if (skusServerSideProps.brandsContent) {
    return (
      <SkusEntirePage
        {...{
          ...skusServerSideProps,
          customBaseUrl: `${envPublic.NEXT_PUBLIC_BASE_URL}/${slug}`,
        }}
      />
    );
  }

  if (page === 'country-page') {
    return (
      <SkusEntirePage
        {...{
          ...skusServerSideProps,
          customBaseUrl: `${envPublic.NEXT_PUBLIC_BASE_URL}/${slug}`,
        }}
        countrySlug={skusServerSideProps.countrySlug}
      />
    );
  }

  return (
    <>
      <style jsx global>
        {`
          .brands_content_content h1 {
            font-size: 2rem;
            font-weight: 400;
          }

          .brands_content_content h2 {
            font-size: 30px;
            font-weight: 500;
          }

          .brands_content_content h3 {
            font-size: 22px;
            font-weight: 500;
            color: #03181c !important;
          }

          .brands_content_content h4 {
            font-size: 16px;
            font-weight: 600;
          }

          .brands_content_content p {
            font-weight: 300;
            font-size: 16px;
            margin-bottom: 1rem;
            line-height: 1.75;
            color: black;
          }

          .brands_content_content strong {
            font-weight: 500;
          }

          .gold_line {
            position: relative;
            padding-left: 25px;
            margin-bottom: 2rem;
          }

          .gold_line::before {
            content: '';
            position: absolute;
            top: 0;
            left: 0;
            height: 100%;
            width: 5px;
            background-color: var(--gold);
            border-radius: 8px;
          }

          .content_content h2,
          .content_content h3,
          .content_content h4,
          .content_content h5 {
            color: black !important;
            padding-bottom: 0.4rem;
          }

          .content_content h2 {
            margin-bottom: 2rem;
          }

          .content_content p + div + h2,
          .content_content p + div + h3 {
            margin-top: 1.5rem;
          }

          .content_content h2 + .gold_line {
            margin-top: 0.5rem;
          }

          .content_content h3 + .gold_line {
            margin-top: 5px;
          }

          .brands_content_content p:has(img) {
            color: #5f6d7e !important;
            display: flex;
            flex-direction: column;
            margin-bottom: 2rem;
          }

          .brands_content_content p:has(img) img {
            width: fit-content;
            border-radius: 20px;
            margin-bottom: 10px;
            max-width: 100%;
          }

          .content_first_para h2 {
            margin-bottom: 2rem;
          }

          .h2_gold_line:after {
            content: '';
            display: block;
            background-color: rgb(194, 173, 125);
            border-radius: 20px;
            max-width: 350px;
            height: 5px;
            margin-top: 10px;
          }

          a .brands_content_content h2:after {
            content: none;
          }

          .gold_line h2,
          .gold_line h3,
          .gold_line h4,
          .gold_line h5 {
            padding-bottom: 6px;
          }

          .brands_content_content a {
            color: black;
          }

          .table_content_content ul {
            padding-left: 15px;
            color: var(--blue);
            display: flex;
            flex-wrap: wrap;
          }

          .table_content_content ul li {
            padding-bottom: 10px;
            font-size: 14px;
            flex: 1 1 50%;
          }

          .table_content_content ul li a {
            color: var(--darkblue);
          }

          .brands_content_content a {
            color: var(--darkblue);
          }

          /* Accordion */

          .faqsection .accordion-item:first-child .accordion-button {
            border-top: none;
          }

          .faqsection .accordion-button {
            background-color: #ededee80;
            border: none;
            border-top: 1px solid #919ba7;
            text-shadow: none;
            margin-left: 0;
            padding-left: 0;
          }

          .faqsection .accordion-body {
            background-color: #ededee80;
            padding-left: 0;
          }

          .faqsection .accordion-item {
            border: none;
          }

          .faqsection .accordion-button:not(.collapsed) {
            background-color: #ededee80;
            box-shadow: none;
          }

          @media only screen and (max-width: 1000px) {
            .table_content_content ul {
              flex-direction: column;
            }

            .brands_content_content img {
              width: 100%;
              max-width: 100%;
            }

            .related_blogs_div .tw-flex.tw-gap-8 {
              flex-direction: column;
            }
          }
        `}
      </style>
      <Container>
        <Col xs={12} className="max-nine:!tw-hidden">
          <IndexNav newSkus={newSkus} newCollections={newCollections} />
        </Col>
        <Col xs={12} className={`tw-mb-5 nine:!tw-hidden`}>
          <CategoryNav userId={null} />
        </Col>
      </Container>
      <div className="tw-w-full tw-bg-white tw-font-inter tw-text-text tw-antialiased">
        <Container>
          {/* Hero Section with Overlay */}
          <div className="tw-relative tw-h-[285px] tw-w-full tw-overflow-hidden tw-rounded-3xl">
            <Swiper
              slidesPerView={1}
              loop={true}
              autoplay={{
                delay: 5000,
                disableOnInteraction: false,
              }}
              modules={[Autoplay]}
              className="tw-h-full tw-w-full tw-rounded-3xl tw-pb-4"
            >
              {page?.banner_image.map(image => {
                return (
                  <SwiperSlide
                    key={JSON.stringify(image)}
                    className="tw-relative tw-h-full tw-w-full tw-rounded-3xl"
                  >
                    <Image
                      src={image.url}
                      alt={image.alternativeText || page?.name || ''}
                      width={1325}
                      height={285}
                      priority
                      className="tw-absolute tw-inset-0 tw-z-10 tw-w-full tw-rounded-3xl tw-object-cover tw-object-center tw-transition-transform hover:tw-scale-105 max-nine:tw-h-[250px] nine:tw-aspect-[1325/285]"
                    />
                  </SwiperSlide>
                );
              })}
            </Swiper>

            {/* Content Overlay */}
            <div className="tw-absolute tw-inset-0 tw-z-20 tw-flex tw-items-center tw-justify-center">
              <div className="tw-px-4 tw-text-left">
                <div
                  className={`tw-absolute tw-left-[3vw] tw-top-1/2 tw-max-w-[90vw] -tw-translate-y-1/2 tw-text-white sm:tw-left-[50px] sm:tw-max-w-[650px]`}
                >
                  <h1
                    className={`tw-font-prata tw-text-2xl tw-font-normal max-nine:tw-text-center sm:tw-text-3xl md:tw-text-4xl lg:tw-text-5xl`}
                    dangerouslySetInnerHTML={{
                      __html: page.banner_headline || '',
                    }}
                  ></h1>
                  <hr
                    className={`tw-w-[250px] tw-rounded-[50px] tw-border-[4px] tw-border-custom-gold tw-opacity-100 max-nine:tw-mx-auto sm:tw-w-[350px]`}
                  ></hr>
                  <h2
                    className={`tw-text-base tw-font-medium tw-leading-relaxed tw-text-[#DEDFE2] max-nine:tw-text-center sm:tw-text-lg sm:tw-leading-[33px] md:tw-text-[18px]`}
                    dangerouslySetInnerHTML={{
                      __html: page.banner_text || '',
                    }}
                  ></h2>
                </div>
              </div>
            </div>
          </div>
        </Container>

        <div className="tw-my-8">
          <SkusEntirePage
            {...{
              ...skusServerSideProps,
              customBaseUrl: `${envPublic.NEXT_PUBLIC_BASE_URL}/${slug}`,
            }}
            includeCategoryNav={false}
          />
        </div>
        <div className={'brands_content_box'}>
          <Col className={`brands_latest_articles tw-mt-[60px] tw-bg-customGray tw-py-[60px]`}>
            <Container>
              <div className="">
                <h2 className={`tw-mb-[15px] tw-text-[24px] tw-font-semibold`}>
                  {translateBlog(BlogEnum.latest_articles)}
                </h2>
                <div
                  className={`tw-mb-[40px] tw-h-[5px] tw-max-w-[100px] tw-rounded-[20px] tw-bg-custom-gold`}
                ></div>
                <RelatedBlogs blogtitle={''}></RelatedBlogs>
                <PrimaryButtonLink
                  href={'/blog'}
                  outlined={true}
                  className={
                    'd-flex mx-auto font-weight-bold justify-content-center tw-mt-[25px] tw-w-[fit-content] tw-w-auto tw-px-[60px]'
                  }
                >
                  {translateBlog(BlogEnum.all_articles)}
                </PrimaryButtonLink>
              </div>
            </Container>
          </Col>
          <Container className="brands_content_content">
            <Col className={`mx-auto tw-mt-[60px]`} md={8}>
              <div
                className={`content_first_para`}
                dangerouslySetInnerHTML={{
                  __html: page?.content_first_para || '',
                }}
              ></div>
              <div
                className={`table_content_content tw-my-[30px] tw-rounded-[20px] tw-bg-customGray tw-p-[30px]`}
              >
                <h3 className={`tw-pb-[20px]`}> {translateBlog(BlogEnum.content_table)}</h3>

                <div
                  dangerouslySetInnerHTML={{
                    __html: page?.content_table || '',
                  }}
                ></div>
              </div>
              <div
                className={`content_content`}
                dangerouslySetInnerHTML={{
                  __html: page?.content_text || '',
                }}
              ></div>
            </Col>
          </Container>
        </div>
      </div>
      <FAQSection faqs={page?.faqs} />
    </>
  );
}
